import { Outlet, matchPath, useLocation, useParams } from "react-router-dom";

import { SurveyDetailBackLink } from "./parts/survey-detail-back-link";
import { SurveyDetailState } from "./parts/survey-detail-state";
import { isApiError } from "~/api";
import { useSurvey } from "~/features/surveys/hooks";
import { surveyDetailRouteList } from "~/features/surveys/lib/survey-detail-routes";
import { useTranslation } from "~/i18n";

export function SurveyDetailLayout() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { uuid } = useParams<{ uuid: string }>();
  const { data, error, isFetching, isPending, refetch } = useSurvey(uuid ?? "");
  const section = surveyDetailRouteList.find(({ path }) =>
    matchPath(`/surveys/:uuid/${path}`, pathname),
  );

  if (!uuid) {
    return (
      <SurveyDetailState error message={t("surveyDetail.errors.notFound")} />
    );
  }

  if (isPending) {
    return <SurveyDetailState message={t("surveyDetail.loading")} />;
  }

  if (!data) {
    const errorKey =
      isApiError(error) && error.status === 404
        ? "surveyDetail.errors.notFound"
        : isApiError(error) && error.status === 403
          ? "surveyDetail.errors.permission"
          : "surveyDetail.errors.generic";
    const canRetry = errorKey === "surveyDetail.errors.generic";

    return (
      <SurveyDetailState
        error
        message={t(errorKey)}
        onRetry={canRetry ? () => void refetch() : undefined}
        retrying={isFetching}
      />
    );
  }

  const sectionTitle = section
    ? "parentLabelKey" in section
      ? `${t(section.parentLabelKey)} - ${t(section.labelKey)}`
      : t(section.labelKey)
    : null;

  return (
    <div className="lg:-m-4">
      <header className="relative rounded-panel bg-surface px-4 py-6 shadow-raised lg:flex lg:min-h-52 lg:items-center lg:rounded-none lg:px-[57px] lg:pb-0 lg:pt-[34px]">
        <div className="lg:absolute lg:left-[57px] lg:top-6">
          <SurveyDetailBackLink />
        </div>
        <h1 className="mt-5 min-w-0 break-words text-3xl font-extrabold leading-tight text-foreground lg:mt-0 lg:text-[40px] lg:leading-[54px]">
          {t("surveyDetail.surveyLabel")} - {data.projectName}
        </h1>
      </header>

      <div className="pt-6 lg:px-16 lg:pt-[43px]">
        {sectionTitle ? (
          <div className="flex min-h-[50px] items-center rounded-panel bg-surface px-5 py-2 shadow-raised lg:px-[34px] lg:py-0">
            <h2 className="text-xl font-extrabold leading-8 text-foreground lg:text-2xl lg:leading-9">
              {sectionTitle}
            </h2>
          </div>
        ) : null}

        <div className="pt-6 lg:pt-[43px]">
          <Outlet context={data} />
        </div>
      </div>
    </div>
  );
}
